import React, { useEffect, useState, useCallback } from 'react';
import { toast } from 'sonner';
import { Mail, Loader2, Archive, Trash2, Reply, CircleDollarSign, MailOpen, RefreshCw, X, Send } from 'lucide-react';
import api, { formatApiError } from '@/lib/adminApi';
import { EmptyState } from '@/components/admin/EmptyState';

const FILTERS = [
  { key: 'all', label: 'All' },
  { key: 'unread', label: 'Unread' },
  { key: 'action', label: '💸 Action needed' },
];

const ReplyBox = ({ msg, onClose }) => {
  const [body, setBody] = useState('');
  const [busy, setBusy] = useState(false);
  const send = async () => {
    if (!body.trim()) return toast.error('Write a reply first');
    setBusy(true);
    try {
      await api.post('/admin/mailbox/reply', { to: msg.from_email, subject: msg.subject, body_html: body });
      toast.success(`Reply sent to ${msg.from_email}`);
      onClose();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  return (
    <div className="mt-4 space-y-2 rounded-2xl border border-brand/20 bg-brand/5 p-4">
      <p className="text-xs text-white/50">Replying to <span className="text-white">{msg.from_email}</span> from noreply@copy.click (their reply goes to info@copy.click)</p>
      <textarea data-testid="mailbox-reply-body" rows={5} autoFocus value={body} onChange={(e) => setBody(e.target.value)}
        className="w-full rounded-xl border border-white/10 bg-elevated px-4 py-2.5 text-sm text-white placeholder:text-white/30 focus:border-brand/50 focus:outline-none" placeholder="Write your reply..." />
      <div className="flex gap-2">
        <button onClick={send} disabled={busy} data-testid="mailbox-reply-send" className="flex items-center gap-2 rounded-xl bg-brand px-4 py-2 text-sm font-semibold text-ink hover:opacity-90 disabled:opacity-40">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Send reply
        </button>
        <button onClick={onClose} className="rounded-xl border border-white/10 px-4 py-2 text-sm text-white/60 hover:bg-white/5">Cancel</button>
      </div>
    </div>
  );
};

export default function AdminMailbox() {
  const [status, setStatus] = useState(null);
  const [messages, setMessages] = useState(null);
  const [filter, setFilter] = useState('all');
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [replying, setReplying] = useState(false);
  const [loading, setLoading] = useState(true);

  const fetchList = useCallback((f) => {
    setLoading(true);
    api.get('/admin/mailbox/messages', { params: { filter: f, limit: 25 } })
      .then((r) => setMessages(r.data.messages))
      .catch((e) => toast.error(formatApiError(e.response?.data?.detail)))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    api.get('/admin/mailbox/status').then((r) => setStatus(r.data));
    fetchList('all');
  }, [fetchList]);

  const openMessage = async (msg) => {
    setSelected(msg.uid);
    setDetail(null);
    setReplying(false);
    try {
      const { data } = await api.get(`/admin/mailbox/messages/${msg.uid}`);
      setDetail(data);
      if (!msg.seen) {
        api.post(`/admin/mailbox/messages/${msg.uid}/read`, { read: true });
        setMessages((prev) => prev.map((x) => (x.uid === msg.uid ? { ...x, seen: true } : x)));
      }
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const act = async (uid, action) => {
    if (action === 'delete' && !window.confirm('Move this email to Trash?')) return;
    try {
      await api.post(`/admin/mailbox/messages/${uid}/${action}`);
      toast.success(action === 'archive' ? 'Archived' : 'Moved to Trash');
      setMessages((prev) => prev.filter((x) => x.uid !== uid));
      if (selected === uid) { setSelected(null); setDetail(null); }
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (status && !status.connected) {
    return (
      <div className="space-y-6">
        <h1 className="font-display text-2xl font-bold text-white sm:text-3xl">Mailbox</h1>
        <EmptyState icon={Mail} title="Mailbox not connected" description={status.error || 'Add IMAP credentials to the backend to read info@copy.click here.'} />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-bold text-white sm:text-3xl">Mailbox</h1>
          <p className="mt-1 text-sm text-white/50">
            {status ? <>Live connection to <span className="text-white">{status.user}</span> — {status.total} emails, <span className="text-brand">{status.unread} unread</span>. No webmail login needed.</> : 'Connecting…'}
          </p>
        </div>
        <button onClick={() => fetchList(filter)} data-testid="mailbox-refresh" className="flex items-center gap-2 rounded-xl border border-white/10 bg-elevated px-4 py-2 text-sm text-white/70 hover:bg-white/5">
          <RefreshCw className="h-4 w-4" /> Refresh
        </button>
      </div>

      <div className="flex gap-2">
        {FILTERS.map((f) => (
          <button key={f.key} data-testid={`mailbox-filter-${f.key}`} onClick={() => { setFilter(f.key); fetchList(f.key); }}
            className={`rounded-xl px-4 py-2 text-sm font-medium transition-colors ${filter === f.key ? 'bg-brand/10 text-brand' : 'text-white/60 hover:bg-white/5 hover:text-white'}`}>
            {f.label}
          </button>
        ))}
      </div>

      <div className="grid gap-4 lg:grid-cols-[380px_1fr]">
        <div className="max-h-[70vh] overflow-y-auto rounded-2xl border border-white/10 bg-surface/70">
          {loading ? (
            <div className="flex h-40 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-brand" /></div>
          ) : !messages || messages.length === 0 ? (
            <p className="px-5 py-10 text-center text-sm text-white/40">{filter === 'action' ? 'Nothing needs your attention 🎉' : 'No emails here.'}</p>
          ) : messages.map((msg) => (
            <button key={msg.uid} data-testid="mailbox-message-row" onClick={() => openMessage(msg)}
              className={`block w-full border-b border-white/5 px-4 py-3 text-left transition-colors hover:bg-white/[0.04] ${selected === msg.uid ? 'bg-brand/5' : ''}`}>
              <div className="flex items-center gap-2">
                {!msg.seen && <span className="h-2 w-2 shrink-0 rounded-full bg-brand" />}
                <p className={`flex-1 truncate text-sm ${msg.seen ? 'text-white/60' : 'font-semibold text-white'}`}>{msg.from_name}</p>
                {msg.payment && <span data-testid="mailbox-payment-flag" className="shrink-0 rounded-full bg-yellow-400/15 px-2 py-0.5 text-[10px] font-bold text-yellow-400">💸 {msg.payment_reason}</span>}
              </div>
              <p className={`mt-0.5 truncate text-sm ${msg.seen ? 'text-white/40' : 'text-white/80'}`}>{msg.subject}</p>
              <p className="mt-0.5 text-[11px] text-white/30">{msg.date ? new Date(msg.date).toLocaleString() : ''}</p>
            </button>
          ))}
        </div>

        <div className="max-h-[70vh] overflow-y-auto rounded-2xl border border-white/10 bg-surface/70 p-5">
          {!selected ? (
            <div className="flex h-full min-h-[300px] flex-col items-center justify-center text-white/30">
              <MailOpen className="h-10 w-10" />
              <p className="mt-3 text-sm">Select an email to read it</p>
            </div>
          ) : !detail ? (
            <div className="flex h-40 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-brand" /></div>
          ) : (
            <div>
              <div className="flex flex-wrap items-start justify-between gap-3 border-b border-white/10 pb-4">
                <div className="min-w-0">
                  <h2 className="font-display text-lg font-bold text-white">{detail.subject}</h2>
                  <p className="mt-1 text-sm text-white/50">{detail.from_name} &lt;{detail.from_email}&gt; · {detail.date ? new Date(detail.date).toLocaleString() : ''}</p>
                  {detail.payment && <span className="mt-2 inline-block rounded-full bg-yellow-400/15 px-2.5 py-1 text-[11px] font-bold text-yellow-400">💸 Payment-related: {detail.payment_reason}</span>}
                </div>
                <div className="flex shrink-0 gap-2">
                  <button onClick={() => setReplying(true)} data-testid="mailbox-reply-btn" title="Reply" className="flex items-center gap-1.5 rounded-lg border border-white/10 px-2.5 py-1.5 text-xs text-white/70 hover:bg-white/5"><Reply className="h-3.5 w-3.5" /> Reply</button>
                  <button onClick={() => act(detail.uid, 'archive')} data-testid="mailbox-archive-btn" title="Archive" className="rounded-lg border border-white/10 p-1.5 text-white/50 hover:bg-white/5"><Archive className="h-3.5 w-3.5" /></button>
                  <button onClick={() => act(detail.uid, 'delete')} data-testid="mailbox-delete-btn" title="Delete" className="rounded-lg border border-white/10 p-1.5 text-white/50 hover:bg-destructive/10 hover:text-destructive"><Trash2 className="h-3.5 w-3.5" /></button>
                  <button onClick={() => { setSelected(null); setDetail(null); }} className="rounded-lg border border-white/10 p-1.5 text-white/50 hover:bg-white/5"><X className="h-3.5 w-3.5" /></button>
                </div>
              </div>
              {replying && <ReplyBox msg={detail} onClose={() => setReplying(false)} />}
              <div className="mt-4">
                {detail.html ? (
                  <iframe title="email" sandbox="" srcDoc={detail.html} className="h-[48vh] w-full rounded-xl border border-white/10 bg-white" />
                ) : (
                  <pre className="whitespace-pre-wrap rounded-xl border border-white/10 bg-elevated p-4 text-sm leading-relaxed text-white/80">{detail.text || '(empty message)'}</pre>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
